import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trophy, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { BoulderTally } from '../../components/competition/BoulderTally';
import { errorCodeOf } from '../../services/api-error';
import {
  roundScoresService,
  type AttemptType,
  type RoundScore,
} from '../../services/round-scores';
import { rankingAttempts, tally } from '../../utils/round-score-summary';

/** The mark each kind of attempt leaves on a card, as a judge writes it. */
const MARK: Record<AttemptType, string> = { attempt: '|', zone: '+', top: 'T' };

interface LiveScoreCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitionSlug: string;
  roundId: string;
  score: RoundScore | null;
  /**
   * Whether the round still takes marks. False once it is closed, which ends
   * everybody's turn: the card is then only there to be read, and the server
   * would refuse a press anyway.
   */
  canScore: boolean;
  /**
   * Whether the board's regulation tells judged blocks from self-certified ones
   * (OPES): the tally then shows the judged share, and a self-certified block
   * says its attempts do not count.
   */
  judgedBlocks?: boolean;
  /**
   * The card as it stands when the dialog closes — every press has already
   * been saved, so this is what the page behind puts in the row.
   */
  onClosed: (score: RoundScore) => void;
  /**
   * The server having answered that the round is closed — said out loud so the
   * page behind, which may have been open since before somebody closed it, can
   * put its own buttons away without being reloaded.
   */
  onRoundClosed?: () => void;
}

/**
 * One person's card for the whole round, one row per block, written on live.
 *
 * The same three buttons as the per-block table beside it, turned the other
 * way: everything this athlete climbs, on one screen. A press is a saved go —
 * the card is sent as it should then read, so pressing twice in a row is safe
 * — and the x on a mark takes it off the same way. There is no save button
 * because there is nothing left to save; the page behind is told the final
 * card when the dialog closes, rather than redrawn under it on every press.
 */
export default function LiveScoreCardDialog({
  open,
  onOpenChange,
  competitionSlug,
  roundId,
  score,
  canScore,
  judgedBlocks = false,
  onClosed,
  onRoundClosed,
}: LiveScoreCardDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [card, setCard] = useState<RoundScore | null>(null);
  /** The one block with a save in flight, so only its own buttons go quiet. */
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (open && score) setCard(score);
  }, [open, score]);

  const write = async (roundClimbId: string, attempts: AttemptType[]) => {
    if (!card || busy || !canScore) return;
    setBusy(roundClimbId);
    try {
      const saved = await roundScoresService.save(competitionSlug, roundId, card.id, [
        { round_climb_id: roundClimbId, attempts },
      ]);
      setCard(saved);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      if (errorCodeOf(error) === 'round_closed') onRoundClosed?.();
    } finally {
      setBusy(null);
    }
  };

  const attemptsOn = (roundClimbId: string): AttemptType[] =>
    card?.cards.find((c) => c.round_climb_id === roundClimbId)?.attempts.map((a) => a.attempt_type) ?? [];

  const add = (roundClimbId: string, type: AttemptType) =>
    write(roundClimbId, [...attemptsOn(roundClimbId), type]);

  const removeAt = (roundClimbId: string, index: number) =>
    write(
      roundClimbId,
      attemptsOn(roundClimbId).filter((_, i) => i !== index)
    );

  const close = (next: boolean) => {
    if (next) return;
    if (card) onClosed(card);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[85svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{card?.full_name}</DialogTitle>
          <DialogDescription>
            {canScore
              ? t('gymAdmin.roundScores.liveDialogDescription')
              : t('gymAdmin.roundScores.closedDialogDescription')}
          </DialogDescription>
          {/* The row's "solved" cell, kept in step with every press. */}
          {card && (
            <BoulderTally
              tally={tally(
                card.cards,
                new Set(card.cards.filter((c) => c.requires_judge).map((c) => c.round_climb_id))
              )}
              showJudged={judgedBlocks}
              attempts={rankingAttempts(card.score)}
              testId="live-card-tally"
            />
          )}
        </DialogHeader>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-2 text-left font-medium">{t('gymAdmin.roundBlocks.block')}</th>
                <th className="px-2 py-2 text-left font-medium">{t('gymAdmin.roundScores.solved')}</th>
                <th className="px-2 py-2 text-left font-medium">{t('gymAdmin.roundBlocks.record')}</th>
              </tr>
            </thead>
            <tbody>
              {(card?.cards ?? []).map((row) => {
                const attempts = row.attempts.map((a) => a.attempt_type);
                const topped = attempts.includes('top');
                const saving = busy === row.round_climb_id;

                return (
                  <tr
                    key={row.round_climb_id}
                    className="border-b last:border-0"
                    data-testid="live-card-row"
                  >
                    <td className="px-2 py-2 align-top font-medium">
                      <span className="flex flex-wrap items-center gap-1.5">
                        {row.climb_name ?? t('gymAdmin.roundScores.unnamedClimb')}
                        {/* Who may write on it, whatever the regulation. */}
                        {row.requires_judge && (
                          <Badge variant="secondary" className="text-xs font-normal" data-testid="requires-judge-badge">
                            {t('competitionRounds.requiresJudge')}
                          </Badge>
                        )}
                      </span>
                    </td>

                    {/* Every go in the order it happened, each with the x that
                        takes it off there and then. */}
                    <td className="px-2 py-2 align-top">
                      <div className="flex flex-wrap items-center gap-1">
                        {attempts.length === 0 && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                        {attempts.map((type, index) => (
                          <span
                            key={`${row.round_climb_id}-${index}`}
                            className={cn(
                              'inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 font-mono text-sm',
                              type === 'zone' && 'border-yellow-600 text-yellow-600',
                              type === 'top' && 'border-green-600 text-green-600',
                              type === 'attempt' && 'text-muted-foreground'
                            )}
                            data-testid={`attempt-${type}`}
                          >
                            {MARK[type]}
                            {canScore && (
                              <button
                                type="button"
                                disabled={!!busy}
                                onClick={() => removeAt(row.round_climb_id, index)}
                                aria-label={t('gymAdmin.roundScores.removeAttempt')}
                                className="rounded-full hover:bg-muted disabled:opacity-50"
                                data-testid="remove-attempt"
                              >
                                <X className="size-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {/* Under OPES a self-certified block's attempts are kept
                          and ignored by the ranking: said where they are written. */}
                      {judgedBlocks && !row.requires_judge && (
                        <p className="mt-1 text-xs text-muted-foreground" data-testid="self-certified-note">
                          {t('competitionRankings.selfCertifiedAttempts')}
                        </p>
                      )}
                    </td>

                    {/* A top ends the card, so there is nothing to add past it
                        — and so does a closed round, which ends them all. */}
                    <td className="px-2 py-2 align-top">
                      {canScore && !topped && (
                        <div className="flex flex-wrap items-center gap-1" data-testid="record-attempt">
                          {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={!!busy}
                            onClick={() => add(row.round_climb_id, 'attempt')}
                            data-testid="add-attempt"
                          >
                            {t('judgeBoulder.attempt')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-yellow-600 text-xs text-yellow-600 hover:bg-yellow-50"
                            disabled={!!busy}
                            onClick={() => add(row.round_climb_id, 'zone')}
                            data-testid="add-zone"
                          >
                            <Plus className="mr-1 size-3" />
                            {t('judgeBoulder.zone')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 border-green-600 text-xs text-green-600 hover:bg-green-50"
                            disabled={!!busy}
                            onClick={() => add(row.round_climb_id, 'top')}
                            data-testid="add-top"
                          >
                            <Trophy className="mr-1 size-3" />
                            {t('judgeBoulder.top')}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
